/**
 * Mix & Match Box Builder
 *
 * Prijsberekening client-side. Shopify Automatic Discounts (met min-quantity
 * per tier) passen de daadwerkelijke korting in de cart toe — de builder laat
 * het eindtotaal zien dat matcht met wat klant in de cart ziet.
 */
(function () {
  'use strict';

  const configEl = document.getElementById('mm-config');
  const productsEl = document.getElementById('mm-products-data');
  if (!configEl || !productsEl) return;

  const CONFIG = JSON.parse(configEl.textContent);
  const PRODUCTS = JSON.parse(productsEl.textContent);
  const PRODUCTS_BY_VARIANT = Object.fromEntries(PRODUCTS.map(p => [p.variant_id, p]));

  const state = {
    cart: new Map(),       // variantId -> quantity
    activeFilters: { licht: '', moeilijkheid: '' },
    previewCount: null,    // null = toont actuele, number = user is schuiver aan 't slepen
    reachedMilestones: new Set(),
  };

  // ========== Helpers ==========
  const fmt = (cents) => {
    const n = (cents / 100);
    return '€' + n.toFixed(2).replace('.', ',');
  };

  const getTotalQty = () => Array.from(state.cart.values()).reduce((a, b) => a + b, 0);

  const getStaffelForQty = (qty) => {
    // Zoek de hoogste staffel waar qty >= min aan voldoet
    let active = CONFIG.staffels[0];
    for (const s of CONFIG.staffels) {
      if (qty >= s.min) active = s;
    }
    return active;
  };

  const calcTotal = (ignoreDiscount = false) => {
    let total = 0;
    for (const [vid, qty] of state.cart) {
      const p = PRODUCTS_BY_VARIANT[vid];
      if (p) total += p.price * qty;
    }
    const qty = getTotalQty();
    const staffel = getStaffelForQty(qty);
    const kortingPct = ignoreDiscount ? 0 : staffel.pct;
    const kortingEur = Math.round(total * kortingPct / 100);
    return { normaal: total, korting: kortingEur, nu: total - kortingEur, staffel, qty };
  };

  // ========== DOM refs ==========
  const refs = {
    count: document.getElementById('mm-count'),
    oud: document.getElementById('mm-oud'),
    nu: document.getElementById('mm-nu'),
    fill: document.getElementById('mm-fill'),
    thumb: document.getElementById('mm-thumb'),
    nudge: document.getElementById('mm-nudge'),
    cta: document.getElementById('mm-add-to-cart'),
    ctaHint: document.getElementById('mm-cta-hint'),
    grid: document.getElementById('mm-grid'),
  };

  // ========== Rendering ==========
  function render(previewQty = null) {
    const actualTotals = calcTotal();
    const displayQty = previewQty !== null ? previewQty : actualTotals.qty;
    const displayStaffel = getStaffelForQty(displayQty);
    const displayTotals = previewQty !== null
      ? previewFakeTotals(previewQty)
      : actualTotals;

    // Counts
    refs.count.textContent = actualTotals.qty;

    // Prijzen (altijd op basis van echte cart)
    if (actualTotals.qty >= 2 && actualTotals.korting > 0) {
      refs.oud.textContent = fmt(actualTotals.normaal);
      refs.nu.textContent = fmt(actualTotals.nu);
    } else {
      refs.oud.textContent = '';
      refs.nu.textContent = fmt(actualTotals.normaal);
    }

    // Slider (op basis van previewQty of actual)
    const pctFill = (displayQty / CONFIG.maxPerBox) * 100;
    refs.fill.style.width = pctFill + '%';
    refs.thumb.style.left = pctFill + '%';
    refs.thumb.setAttribute('aria-valuenow', displayQty);

    // Stops: markeer bereikt
    document.querySelectorAll('.mm__slider-stop').forEach(stop => {
      const n = parseInt(stop.dataset.stop, 10);
      stop.classList.toggle('mm__slider-stop--reached', n <= actualTotals.qty && n > 0);
    });

    // Nudge
    refs.nudge.innerHTML = buildNudge(actualTotals, previewQty, displayStaffel);

    // CTA
    const canCheckout = actualTotals.qty >= 2;
    refs.cta.disabled = !canCheckout;
    refs.ctaHint.textContent = canCheckout
      ? `Jouw box: ${actualTotals.qty} planten voor ${fmt(actualTotals.nu)}`
      : 'Kies minstens 2 planten voor korting.';

    // Cards: toon actieve qty + "in box" styling
    document.querySelectorAll('.mm-card').forEach(card => {
      const vid = parseInt(card.dataset.mmVariantId, 10);
      const qty = state.cart.get(vid) || 0;
      card.classList.toggle('mm-card--in-box', qty > 0);
      const qtyEl = card.querySelector('[data-mm-qty]');
      if (qtyEl) qtyEl.textContent = qty;
      // disable + knop als max bereikt
      const plusBtn = card.querySelector('[data-mm-action="plus"]');
      const minusBtn = card.querySelector('[data-mm-action="minus"]');
      if (plusBtn) plusBtn.disabled = actualTotals.qty >= CONFIG.maxPerBox;
      if (minusBtn) minusBtn.disabled = qty === 0;
    });
  }

  function previewFakeTotals(qty) {
    // Voor preview-modus: toon theoretische prijs bij qty×€10,95
    const avg = 1095;
    const total = qty * avg;
    const staffel = getStaffelForQty(qty);
    const korting = Math.round(total * staffel.pct / 100);
    return { normaal: total, korting, nu: total - korting, staffel, qty };
  }

  function buildNudge(totals, previewQty, displayStaffel) {
    const qty = totals.qty;
    const staffel = totals.staffel;

    if (previewQty !== null && previewQty !== qty) {
      return `<em>Preview:</em> ${previewQty} planten → <strong>${displayStaffel.pct}% korting</strong>${displayStaffel.label ? ' — ' + displayStaffel.label : ''}`;
    }

    if (qty === 0) {
      return 'Voeg planten toe om korting te zien.';
    }
    if (qty === 1) {
      return `Nog <strong>1 plant</strong> voor <strong>5% korting</strong> (Duo).`;
    }
    const nextStaffel = CONFIG.staffels.find(s => s.min === qty + 1);
    if (qty >= CONFIG.maxPerBox) {
      return `🎉 <strong>${staffel.label}</strong> — ${staffel.pct}% korting, max bereikt!`;
    }
    if (nextStaffel && nextStaffel.pct > staffel.pct) {
      return `Nog <strong>1 plant</strong> voor <strong>${nextStaffel.pct}% korting</strong> (${nextStaffel.label}).`;
    }
    return `<strong>${staffel.label}</strong> — ${staffel.pct}% korting op je box.`;
  }

  // ========== Milestone celebration ==========
  function checkMilestones() {
    const qty = getTotalQty();
    const staffel = getStaffelForQty(qty);
    const key = staffel.min;
    if (key > 1 && !state.reachedMilestones.has(key)) {
      state.reachedMilestones.add(key);
      refs.thumb.classList.add('mm__slider-thumb--celebrate');
      setTimeout(() => refs.thumb.classList.remove('mm__slider-thumb--celebrate'), 500);
    }
  }

  // ========== Qty button handlers ==========
  function changeQty(variantId, delta) {
    const current = state.cart.get(variantId) || 0;
    const totalQty = getTotalQty();
    if (delta > 0 && totalQty >= CONFIG.maxPerBox) return;
    const next = Math.max(0, current + delta);
    if (next === 0) state.cart.delete(variantId);
    else state.cart.set(variantId, next);
    render();
    if (delta > 0) checkMilestones();
  }

  refs.grid.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-mm-action]');
    if (!btn) return;
    const card = btn.closest('.mm-card');
    if (!card) return;
    const vid = parseInt(card.dataset.mmVariantId, 10);
    changeQty(vid, btn.dataset.mmAction === 'plus' ? 1 : -1);
  });

  // ========== Filters ==========
  function applyFilters() {
    document.querySelectorAll('.mm-card').forEach(card => {
      const licht = card.dataset.mmLicht || '';
      const mo = card.dataset.mmMoeilijkheid || '';
      const matchLicht = !state.activeFilters.licht || licht === state.activeFilters.licht;
      const matchMo = !state.activeFilters.moeilijkheid || mo === state.activeFilters.moeilijkheid;
      card.style.display = (matchLicht && matchMo) ? '' : 'none';
    });
  }

  document.querySelectorAll('.mm__filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const filter = btn.dataset.filter;
      const value = btn.dataset.value;
      state.activeFilters[filter] = value;
      // Visueel: alleen 1 actief per groep
      document.querySelectorAll(`.mm__filter-btn[data-filter="${filter}"]`).forEach(b => {
        b.classList.toggle('mm__filter-btn--active', b === btn);
      });
      applyFilters();
    });
  });

  // ========== Slider preview ==========
  document.querySelectorAll('.mm__slider-stop').forEach(stop => {
    const n = parseInt(stop.dataset.stop, 10);
    const show = () => render(n);
    const hide = () => render();
    stop.addEventListener('mouseenter', show);
    stop.addEventListener('focus', show);
    stop.addEventListener('mouseleave', hide);
    stop.addEventListener('blur', hide);
  });

  // ========== Add to cart ==========
  refs.cta.addEventListener('click', async () => {
    const items = Array.from(state.cart.entries()).map(([vid, qty]) => ({
      id: vid, quantity: qty
    }));
    if (items.length === 0) return;

    refs.cta.disabled = true;
    refs.cta.textContent = 'Toevoegen…';

    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!r.ok) throw new Error('Toevoegen mislukt (HTTP ' + r.status + ')');
      // Open cart drawer indien beschikbaar
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
      } else {
        window.location.href = '/cart';
      }
    } catch (err) {
      console.error('[mm] add to cart error', err);
      alert('Er ging iets mis bij het toevoegen aan je winkelwagen. Probeer opnieuw.');
      refs.cta.disabled = false;
      refs.cta.textContent = 'Voeg box toe aan winkelwagen';
    }
  });

  // Initial render
  render();
})();
